// The MODULE GLYPHS — one icon name per workspace module, exactly the set the Figma "Universal
// inbox" frame draws (node 40008716-284885): global, sms, tick-circle, edit, calendar-2 and the
// two-person profile. It is the icon twin of @/lib/moduleColors: that file says what colour a
// module is, this one says what shape it is, and neither is written at a call site any more.
//
// It exists for the same reason the palette did — the names had drifted per surface. The creation
// strip drew an email as `menu-email` (a filled envelope-in-a-box) and a contact as
// `menu-contacts`, while the Ongoing list drew an email as `email`, a task as `tasks`, a note as
// `notes` and an event as `events`: three different envelopes for one module depending on where
// you looked. One map, so a module can never be two shapes on two surfaces.
//
// On `contact`: the repo's `contacts.svg` IS vuesax's profile-2user — the same four paths as the
// Figma export, scaled 24→16 — so the canonical name stays `contacts` rather than adding a
// duplicate glyph file under a second name.
const MODULE_ICON = {
  event: 'calendar-2',
  task: 'tick-circle',
  research: 'global',
  note: 'edit',
  email: 'sms',
  contact: 'contacts',
  // The seventh module (busy-timeline-srv#29): SMS / WhatsApp-style messages and call logs from
  // busy-source-srv. Named `messaging`, NOT `message` — `message` is the alias the Global timeline
  // panel has always used for an EMAIL (see ALIAS below) and stays that. The bubble glyph is the
  // pack's `message-text`; a call swaps in `phone` at the call site (lib/sources.js).
  messaging: 'message-text',
}

// The same three vocabularies moduleColors accommodates (the BFF's "email", the panel's "message",
// the old icon names "events"/"tasks"/"notes", the design's "calendar"/"web-search"/"reminder"),
// kept deliberately identical to that file's ALIAS so a kind that has a colour also has a glyph.
const ALIAS = {
  calendar: 'event',
  events: 'event',
  tasks: 'task',
  reminder: 'note',
  notes: 'note',
  message: 'email',
  emails: 'email',
  mail: 'email',
  search: 'research',
  websearch: 'research',
  'web-search': 'research',
  web: 'research',
  global: 'research',
  // A Research PACK is the research module's own thing (Pouria, 2026-10-01, frame
  // 40011046:161537 draws "Best sun hat for travel" in the research globe, research blue):
  // busy-timeline-srv#42 names an entity offer's module `researchpack`, and the app's own
  // spellings of the same noun land here too, rather than on the fallback.
  researchpack: 'research',
  researchpacks: 'research',
  'research-pack': 'research',
  'research-packs': 'research',
  research_pack: 'research',
  contacts: 'contact',
  person: 'contact',
  people: 'contact',
  // The message sources (busy-timeline-srv#29), and the box's own mode name for the call log.
  sms: 'messaging',
  whatsapp: 'messaging',
  call: 'messaging',
  calls: 'messaging',
  messages: 'messaging',
}

// The canonical module name for any of the vocabularies above; '' for anything unrecognised, so a
// caller can tell "not a module" from "a module we have no glyph for".
export function moduleIconKey(type) {
  const key = String(type || '').trim().toLowerCase()
  const canon = ALIAS[key] || key
  return MODULE_ICON[canon] ? canon : ''
}

// The module's icon-pack name. Anything unrecognised reads as research — the globe is what the
// Ongoing list has always fallen back to, so an unknown row is drawn rather than left blank.
export function moduleIcon(type) {
  return MODULE_ICON[moduleIconKey(type)] || MODULE_ICON.research
}

// A copy for callers that need the whole map (never the map itself — nothing may edit the set).
export function moduleIcons() {
  return { ...MODULE_ICON }
}
